import { Head } from 'vite-react-ssg'
import { useLocation } from 'react-router-dom'

const SITE = 'https://calculatemypeptides.com'

/*
 * Per-page <head> tags that are PRERENDERED into the HTML source (title, meta
 * description, canonical, Open Graph / Twitter, and optional JSON-LD). Works in
 * both SSG and on the client (wraps react-helmet). Replaces the old client-only
 * useSeo hook so search engines and AI see the right tags in the raw HTML.
 *
 * A default <Seo> renders in the app Layout; per-page <Seo> instances override
 * it (react-helmet keeps the deepest-rendered title / matching meta). Canonical
 * + og:url are derived from the current path so every prerendered page points at
 * itself — the old setup hardcoded the homepage URL as canonical on every page.
 */
export default function Seo({ title, description, canonical, jsonLd, breadcrumbs, image, noindex = false }) {
  const { pathname } = useLocation()
  // normalise: strip trailing slash except for root
  const path = canonical || (pathname === '/' ? '/' : pathname.replace(/\/$/, ''))
  const url = SITE + path
  // og/twitter image: per-type when given, else the branded default. Absolute URL
  // (social scrapers require it). These are helmet-managed here, not index.html.
  const imgUrl = SITE + (image || '/og/default.png')
  // Clamp the meta description to ~155 chars at a word boundary so it isn't
  // truncated mid-word in the SERP (Screaming Frog / Google ~155-char guidance).
  const desc = !description ? undefined
    : description.length <= 160 ? description
    : description.slice(0, 158).replace(/\s+\S*$/, '') + '…'
  // jsonLd may be a single schema object or an array of them.
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean) : []
  // breadcrumbs: [{ name, path }] → BreadcrumbList structured data (helps Google
  // show the page's place in the hierarchy). The array is the trail incl. current.
  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 1) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: SITE + (b.path === '/' ? '/' : b.path.replace(/\/$/, '')),
      })),
    })
  }
  // Robots is helmet-managed (index.html no longer hardcodes it): the Layout's
  // default <Seo> emits index,follow; a page passing noindex overrides it, so
  // utility/auth pages stay out of the index without duplicate-content noise.
  const robots = noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'

  return (
    <Head>
      {title && <title>{title}</title>}
      {title && <meta property="og:title" content={title} />}
      {title && <meta name="twitter:title" content={title} />}
      {desc && <meta name="description" content={desc} />}
      {desc && <meta property="og:description" content={desc} />}
      {desc && <meta name="twitter:description" content={desc} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imgUrl} />
      <meta name="twitter:image" content={imgUrl} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Head>
  )
}
